// Monotone chain convex hull (Andrew's algorithm)
function cross(O, A, B) {
  return (A.lng - O.lng) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lng - O.lng);
}

function convexHull(points) {
  if (points.length < 3) return [...points];
  const pts = [...points].sort((a, b) => a.lng !== b.lng ? a.lng - b.lng : a.lat - b.lat);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function haversineM(p1, p2) {
  const R = 6371000;
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function interpolateEdge(p1, p2, spacingM) {
  const dist = haversineM(p1, p2);
  const n = Math.max(1, Math.floor(dist / spacingM));
  const pts = [];
  for (let i = 1; i < n; i++) {
    const t = i / n;
    pts.push({ lat: p1.lat + (p2.lat - p1.lat) * t, lng: p1.lng + (p2.lng - p1.lng) * t });
  }
  return pts;
}

/**
 * Treats ALL obstacles in the project as one building footprint.
 * Computes the convex hull of all valid coordinates, interpolates perimeter
 * points every spacingM metres, and returns an expanded list where every
 * perimeter point carries the maximum elevation across all obstacles.
 * Falls through unchanged if fewer than 3 obstacles have valid coordinates.
 */
export function computeHullObstacles(obstacles, spacingM = 10) {
  const valid = obstacles.filter(o => o.latitude != null && o.longitude != null);

  if (valid.length < 3) return obstacles;

  const maxElev = Math.max(...valid.map(o => parseFloat(o.elevation_m) || 0));
  const pts = valid.map(o => ({ lat: o.latitude, lng: o.longitude }));
  const hull = convexHull(pts);

  const perimPts = [];
  for (let i = 0; i < hull.length; i++) {
    const p1 = hull[i];
    const p2 = hull[(i + 1) % hull.length];
    perimPts.push(p1);
    perimPts.push(...interpolateEdge(p1, p2, spacingM));
  }

  const base = valid[0];
  return perimPts.map((pt, i) => ({
    ...base,
    id: `hull_${base.id}_${i}`,
    latitude: pt.lat,
    longitude: pt.lng,
    elevation_m: maxElev,
  }));
}

/**
 * Returns a single hull polygon covering ALL obstacles (≥3 valid points).
 * Entry: { name, points: [{lat, lng}], maxElev, count }
 * Used to draw the hull outline on maps.
 */
export function getHullPolygons(obstacles) {
  const valid = obstacles.filter(o => o.latitude != null && o.longitude != null);
  if (valid.length < 3) return [];

  const maxElev = Math.max(...valid.map(o => parseFloat(o.elevation_m) || 0));
  const pts = valid.map(o => ({ lat: o.latitude, lng: o.longitude }));
  const hull = convexHull(pts);
  if (hull.length < 3) return [];

  return [{ name: 'Building Footprint', points: hull, maxElev, count: valid.length }];
}

export const HULL_MODE_KEY = (projectId) => `tsela-hull-mode-${projectId}`;
